import { useState } from 'react';
import { Button, Center, Group, Loader, Paper, SimpleGrid, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { getErrorMessage } from '@/lib/error-message';
import { useOrg } from '@/hooks/use-org';
import { OrganizationSettingsSubscription, UpdateOrganizationMutation } from './-components/graphql';

export const Route = createFileRoute('/_authed/settings/general')({
  component: SettingsGeneralPage,
});

function GeneralForm({
  organizationId,
  initialName,
  initialSlug,
  createdAt,
}: {
  organizationId: string;
  initialName: string;
  initialSlug: string;
  createdAt: string;
}) {
  const [name, setName] = useState(initialName);
  const [slug, setSlug] = useState(initialSlug);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [, updateOrganization] = useMutation(UpdateOrganizationMutation);

  const save = async () => {
    setIsSaving(true);
    setError(null);

    const result = await updateOrganization({ organizationId, name: name.trim(), slug: slug.trim() });

    setIsSaving(false);

    if (result.error) setError(getErrorMessage(result.error, 'Failed to save organization settings'));
  };

  return (
    <Paper p="lg" radius="lg" withBorder>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Stack gap="md">
          <ErrorAlert message={error} />
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label="Name" value={name} onChange={(event) => setName(event.currentTarget.value)} />
            <TextInput
              label="Slug"
              value={slug}
              onChange={(event) => setSlug(event.currentTarget.value)}
              styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
            />
          </SimpleGrid>
          <Text size="xs" c="dimmed">
            Created {new Date(createdAt).toLocaleDateString()}
          </Text>
          <Group justify="flex-end">
            <Button
              type="submit"
              leftSection={<IconDeviceFloppy size={16} />}
              loading={isSaving}
              disabled={!name.trim() || !slug.trim()}
            >
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  );
}

function SettingsGeneralPage() {
  const { organizationId } = useOrg();

  const [{ data }] = useSubscription({
    query: OrganizationSettingsSubscription,
    variables: { organizationId: organizationId ?? '' },
    pause: !organizationId,
  });

  const organization = data?.organizationsByPk ?? null;

  return (
    <Stack gap="lg">
      <div>
        <Title order={2}>General Settings</Title>
        <Text size="sm" c="dimmed" mt="2xs">
          Update your organization name and identifier.
        </Text>
      </div>

      {!organizationId || !organization ? (
        <Center h="30vh">
          <Loader size="lg" />
        </Center>
      ) : (
        <GeneralForm
          key={organization.id}
          organizationId={organizationId}
          initialName={organization.name}
          initialSlug={organization.slug}
          createdAt={organization.createdAt}
        />
      )}
    </Stack>
  );
}
